import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { addUnit, deleteUnit, fetchUnits, updateUnit } from "../../../actions/unitAction";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import { sortHelper } from "../../../utils/helpers/sortHelper";
import FormUnit from "./FormUnit";

const initialState = {
  id: "",
  value: "",
  label: "",
  name: "",
  base_unit_id: null,
  factor: 1,
  description: "",
};

const UnitPage = (props) => {
  const { dataUnits, units, isLoading, error, fetchUnits, addUnit, updateUnit, deleteUnit } = props;

  const [perPage, setPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";
  const [filterText, setFilterText] = useState(searchFromUrl);
  const [formState, setFormState] = useState(initialState);
  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const openModal = () => {
    setFormState(initialState);
    setIsEdit(false);
  };

  const closeModal = () => {
    setFormState(initialState);
    setIsEdit(false);
  };

  const refreshUnits = () => fetchUnits(perPage, currentPage, searchFromUrl);

  const handleAdd = async () => {
    const success = await addUnit({
      ...formState,
      base_unit_id: formState.base_unit_id ? Number(formState.base_unit_id) : null,
      factor: Number(formState.factor),
    });

    if (success) {
      closeModal();
      refreshUnits();
    }

    return success;
  };

  const handleUpdate = async () => {
    const success = await updateUnit(formState.id, {
      ...formState,
      base_unit_id: formState.base_unit_id ? Number(formState.base_unit_id) : null,
      factor: Number(formState.factor),
    });

    if (success) {
      closeModal();
      refreshUnits();
    }

    return success;
  };

  const handleDelete = async (id) => {
    const success = await deleteUnit(id);
    if (success) {
      closeModal();
      refreshUnits();
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setFilterText(value);

    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    setFilterText(searchFromUrl);
  }, [searchFromUrl]);

  const debouncedFetch = useMemo(
    () =>
      debounce((page, limit, search) => {
        fetchUnits(limit, page, search);
      }, 500),
    [fetchUnits],
  );

  useEffect(() => {
    debouncedFetch(currentPage, perPage, searchFromUrl);

    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, perPage, searchFromUrl, debouncedFetch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFromUrl]);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Data Unit / Satuan</h1>
              <div className="page-pretitle">master unit</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  type="button"
                  className="btn btn-primary btn-5 d-none d-sm-inline-block"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form"
                  onClick={openModal}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-2">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Tambah unit
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-6 d-sm-none btn-icon"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form"
                  onClick={openModal}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-2">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="row row-deck row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-table">
                  <div className="card-header">
                    <div className="row w-full">
                      <div className="col-md-9 col-12">
                        <h3 className="card-title mb-0">Unit / Satuan</h3>
                        <p className="text-secondary m-0">Master satuan material dan produk</p>
                      </div>
                      <div className="col-md-3 col-12 my-md-0 my-2">
                        <div className="input-group input-group-flat w-auto">
                          {isLoading ? (
                            <span className="input-group-text">
                              <div
                                className="spinner-border spinner-border-sm text-primary text-center"
                                role="status"></div>
                            </span>
                          ) : (
                            <span className="input-group-text">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="icon icon-1">
                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                                <path d="M21 21l-6 -6" />
                              </svg>
                            </span>
                          )}
                          <input
                            id="advanced-table-search"
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            onChange={handleSearchChange}
                            placeholder="Cari..."
                            value={filterText}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {error ? <div className="alert alert-danger m-3 mb-0">{error}</div> : null}

                  <div id="advanced-table">
                    <div className="table-responsive">
                      <table className="table table-vcenter table-selectable">
                        <thead className="table-header">
                          <tr>
                            <th className="sort-name py-3 w-1">No.</th>
                            <sortHelper.SortableHeader
                              label="Kode"
                              sortKey="value"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <sortHelper.SortableHeader
                              label="Label"
                              sortKey="label"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <sortHelper.SortableHeader
                              label="Nama"
                              sortKey="name"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <th className="sort-status fw-bold">Unit Dasar</th>
                            <sortHelper.SortableHeader
                              label="Faktor"
                              sortKey="factor"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <th className="sort-status fw-bold">Deskripsi</th>
                            <th className="text-start" style={{ width: "20%" }}></th>
                          </tr>
                        </thead>
                        <tbody className="table-tbody">
                          {isLoading ? (
                            [...Array(units?.length || 5)].map((_, index) => (
                              <tr key={`loading-${index}`} style={{ cursor: "loader" }}>
                                <td className="sort-name py-4">{(currentPage - 1) * perPage + index + 1}.</td>
                                {[...Array(6)].map((__, placeholderIndex) => (
                                  <td key={placeholderIndex}>
                                    <div className="placeholder placeholder-lg w-75"></div>
                                  </td>
                                ))}
                                <td className="justify-content-start text-start flex-nowrap">
                                  <div className="placeholder placeholder-lg me-2" style={{ width: "40%" }}></div>
                                  <div className="placeholder placeholder-lg" style={{ width: "40%" }}></div>
                                </td>
                              </tr>
                            ))
                          ) : units.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center text-secondary py-5">
                                Belum ada data unit.
                              </td>
                            </tr>
                          ) : (
                            getSortedData(units).map((unit, index) => (
                              <tr key={unit.id}>
                                <td className="sort-name">{(currentPage - 1) * perPage + index + 1}.</td>
                                <td className="sort-id">
                                  <span className="badge fw-bold">{unit.value}</span>
                                </td>
                                <td>{unit.label}</td>
                                <td>{unit.name}</td>
                                <td>
                                  {unit.base_unit_label ? `${unit.base_unit_label} (${unit.base_unit_value})` : "-"}
                                </td>
                                <td>{Number(unit.factor).toLocaleString("id-ID", { maximumFractionDigits: 4 })}</td>
                                <td className="text-secondary">{unit.description || "-"}</td>
                                <td>
                                  <div className="btn-list flex-nowrap justify-content-center">
                                    <button
                                      type="button"
                                      className="btn btn-1 bg-secondary-lt"
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-form"
                                      aria-label={`Edit unit ${unit.name}`}
                                      onClick={() => {
                                        setIsEdit(true);
                                        setFormState({
                                          id: unit.id,
                                          value: unit.value || "",
                                          label: unit.label || "",
                                          name: unit.name || "",
                                          base_unit_id: unit.base_unit_id || null,
                                          factor: unit.factor || 1,
                                          description: unit.description || "",
                                        });
                                      }}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="icon icon-tabler icons-tabler-outline icon-tabler-edit">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                        <path d="M16 5l3 3" />
                                      </svg>{" "}
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-1 bg-danger-lt"
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-alert-unit"
                                      aria-label={`Hapus unit ${unit.name}`}
                                      onClick={() => {
                                        setFormState({ ...initialState, id: unit.id, name: unit.name });
                                      }}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="icon icon-tabler icons-tabler-outline icon-tabler-trash">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M4 7l16 0" />
                                        <path d="M10 11l0 6" />
                                        <path d="M14 11l0 6" />
                                        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                        <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                      </svg>{" "}
                                      Hapus
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="card-footer d-flex align-items-center">
                      <div className="dropdown">
                        <button className="btn dropdown-toggle" data-bs-toggle="dropdown">
                          <span id="page-count" className="me-1">
                            {perPage}
                          </span>
                          <span>records</span>
                        </button>
                        <div className="dropdown-menu">
                          {[10, 20, 50, 100].map((value) => (
                            <button key={value} className="dropdown-item" onClick={() => handlePerPageChange(value)}>
                              {value} records
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="d-flex">
                        <p className="ms-2 my-auto text-secondary">
                          {dataUnits?.per_page || 0} dari <span>{dataUnits?.total || 0}</span> <span>data</span>
                        </p>
                      </div>

                      <ul className="pagination m-0 ms-auto">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((previous) => Math.max(previous - 1, 1))}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M15 6l-6 6l6 6" />
                            </svg>
                          </button>
                        </li>

                        {dataUnits?.links?.slice(1, -1)?.map((item, index) => {
                          const pageNumber = item.url ? new URL(item.url).searchParams.get("page") : null;

                          return (
                            <li key={index} className={`page-item ${item.active ? "active" : ""}`}>
                              <button
                                className="page-link"
                                disabled={!pageNumber}
                                onClick={() => pageNumber && setCurrentPage(Number(pageNumber))}>
                                {item.label}
                              </button>
                            </li>
                          );
                        })}

                        <li className={`page-item ${currentPage === dataUnits?.last_page ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            disabled={!dataUnits?.next_page_url}
                            onClick={() => setCurrentPage(Number(dataUnits?.current_page) + 1)}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M9 6l6 6l-6 6" />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FormUnit
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        units={units}
        isLoading={isLoading}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
      />

      <AlertConfirmModal
        type="error"
        message={`Menghapus data ${formState.name}`}
        confirmText="Hapus"
        cancelText="Batal"
        idModal="alert-unit"
        idData={formState.id}
        onConfirm={handleDelete}
        onCancel={closeModal}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  dataUnits: state?.units?.datas,
  units: state?.units?.datas?.data || [],
  isLoading: state?.units?.loading,
  error: state?.units?.error,
});

export default connect(mapStateToProps, { fetchUnits, addUnit, updateUnit, deleteUnit })(UnitPage);
